/* ─────────────────────────────────────────────────────────────
   Shop — the part that changes with who is reading.

   Deliberately not inside os.js. os.js fires an "os:role" event for
   exactly this, and keeping the shop out of it means a page that
   does not have a shop on it loads none of this, and a change here
   cannot break the chrome that every other page depends on.

   Two things move: the line at the top, and the order of the group
   rows. The order is done with CSS `order`, not by moving nodes, so
   images already fetched are not fetched again and nothing reflows
   from scratch when the role changes.
   ───────────────────────────────────────────────────────────── */
(function () {
  "use strict";

  function T(k, en) { var t = window.OS_T; return (t && typeof t[k] === "string" && t[k]) || en; }

  var D = document;
  var root = D.querySelector("[data-os-shop]");
  if (!root) return;

  var C = window.OS_CONTENT;
  if (!C || !C.SHOP) return;

  /* Copy typed into the theme editor wins over the deck, the same
     way every other OS section works. */
  var override = {};
  (function () {
    var node = D.querySelector('[data-os-copy]');
    var nodes = D.querySelectorAll('[data-os-copy]');
    for (var i = 0; i < nodes.length; i++) {
      var data;
      try { data = JSON.parse(nodes[i].textContent); } catch (e) { continue; }
      /* A field still holding the line the section shipped with is
         dropped, so the deck's current line shows (see RETIRED in
         os-content.js). Edited fields still win. */
      if (C.unretire) C.unretire(data);
      if (data && data.scope === "shop" && data.head) override = data.head;
    }
    return node;
  })();

  function headFor(id) {
    var o = override[id];
    if (o && (o.h || o.n)) return { h: o.h || "", n: o.n || "" };
    return C.pick(C.SHOP.head, id) || C.SHOP.head.default;
  }

  var groups = root.querySelector("[data-shop-groups]");

  function orderGroups(id) {
    if (!groups) return;
    var seq = C.pick(C.SHOP.order, id) || C.SHOP.order.default;
    var rows = groups.querySelectorAll("[data-shop-group]");
    var lead = C.pick(C.SHOP.lead, id) || "";
    var promoted = seq[0];
    var seenPromoted = false;

    for (var i = 0; i < rows.length; i++) {
      var key = rows[i].getAttribute("data-shop-group");
      var at = seq.indexOf(key);
      /* A group the deck does not mention still has to land
         somewhere, so it goes after everything it does. */
      rows[i].style.order = at === -1 ? seq.length + 1 : at;

      var p = rows[i].querySelector("[data-shop-lead]");
      if (!p) continue;
      /* The lead line belongs to the first row of the promoted
         group, and only when a group was actually promoted. */
      var wants = lead && key === promoted && !seenPromoted;
      if (wants) seenPromoted = true;
      p.textContent = wants ? lead : "";
      p.hidden = !wants;
    }
  }

  /* ── The spec line on a card ────────────────────────────────
     A card says what the instrument measures, not what it is
     called. Those lines are already written, once, in the stage
     decks — "TDS 0–30% · ±0.02% · ~4 s" — so they are read from
     there rather than typed out a second time and left to drift.

     The deck keys instruments by slug and Shopify keys them by
     product handle, so the two are paired here. Same pairing as
     sections/os-learn.liquid uses for the cart.

     A card whose spec Shopify already supplied — a metafield or a
     product type — keeps it; this only fills the empty ones. */
  var HANDLE_TO_SLUG = {
    "difluid-omix": "omix-plus",
    "omni": "omni",
    "r2-extract": "r2-extract",
    "microbalance-ti": "microbalance-ti",
    "coffmeter-m1": "coffmeter-m1",
    "coffmeter-w1": "coffmeter-w1",
    "coffmeter-a1-by-difluid": "coffmeter-a1",
    "difluid-omniflux-live-coffee-roasting-color-tracker": "omniflux",
    "difluid-airwave": "airwave",
    "brew-control-system-ti-microbalance-ti-r2-extract": "brew-control-ti"
  };

  var SPECS = (function () {
    var out = {};
    var stages = C.STAGES || {};
    Object.keys(stages).forEach(function (k) {
      (stages[k].tools || []).forEach(function (t) {
        if (t.slug && t.spec && !out[t.slug]) out[t.slug] = t.spec;
      });
    });
    return out;
  })();

  var JOBS = (C.SHOP && C.SHOP.jobs) || {};

  /* Both lines are filled in one pass over the cards. They were two
     passes and two queries for a while; on the full catalogue that is
     ninety-two walks of the same list to write two strings. */
  var filled = false;
  function fillCards() {
    if (filled) return;
    filled = true;
    var cards = root.querySelectorAll(".os-p[data-handle]");
    for (var i = 0; i < cards.length; i++) {
      var handle = cards[i].getAttribute("data-handle");

      var spec = cards[i].querySelector(".os-p__spec");
      if (spec && !spec.textContent.trim()) {
        var slug = HANDLE_TO_SLUG[handle];
        var s = slug && SPECS[slug];
        if (s) spec.textContent = s;
      }

      /* The job line is hidden rather than empty when there is
         nothing to say, so an unknown product does not leave a gap
         above its name where the other cards have a line. */
      var job = cards[i].querySelector(".os-p__job");
      if (job && !job.textContent.trim()) {
        var j = JOBS[handle];
        if (j) { job.textContent = j; job.hidden = false; }
      }
    }
  }

  function paintHead(id) {
    var head = headFor(id);
    var h = root.querySelector("[data-shop-h]");
    var n = root.querySelector("[data-shop-note]");
    var e = root.querySelector("[data-shop-eyebrow]");
    if (e) e.textContent = T("shop.eyebrow", "Shop");
    if (h && head && head.h) h.textContent = head.h;
    if (n && head && head.n) n.textContent = head.n;
  }

  function paint(id) {
    paintHead(id);
    orderGroups(id);
    fillCards();
  }

  /* ── The role change, as a movement ─────────────────────────
     The rows are already in the page and already have their
     pictures; a role change only rearranges them. So rather than
     rebuild anything, each row is measured where it is, given its
     new order, measured again, and then carried from the old
     position to the new one. The reader sees the shop reorganise
     itself, which is the actual claim the page is making.

     Three things keep this cheap enough for a slow phone:

     - only rows that actually moved are animated;
     - only rows near the viewport are animated, because a row four
       screens down arriving in place is a transition nobody sees
       and every device pays for;
     - transform only, on a handful of elements, with will-change
       set for the length of the movement and taken off after.

     Two layout reads, both deliberate and both batched — every
     measurement happens before any style is written. */
  var MOVE_MS = 460;
  var EASE = "cubic-bezier(0.22, 0.85, 0.24, 1)";

  function reduced() {
    if (window.OS && typeof window.OS.reducedMotion === "function") {
      return window.OS.reducedMotion();
    }
    return window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  function docTop(el) {
    return el.getBoundingClientRect().top + window.pageYOffset;
  }

  function slide(id) {
    var rows = groups
      ? [].slice.call(groups.querySelectorAll("[data-shop-group]"))
      : [];
    if (!rows.length) { paint(id); return; }

    /* FIRST — every read together, before a single write. */
    var before = rows.map(docTop);

    orderGroups(id);
    fillCards();

    /* LAST — one forced layout, then all the reads. */
    var after = rows.map(docTop);

    var vh = window.innerHeight || 800;
    var from = window.pageYOffset - vh * 0.5;
    var to = window.pageYOffset + vh * 1.5;

    var playing = [];
    for (var i = 0; i < rows.length; i++) {
      var dy = before[i] - after[i];
      if (Math.abs(dy) < 2) continue;
      /* Near enough to be watched, in either the place it left or
         the place it is going. */
      var near = (before[i] > from && before[i] < to) ||
                 (after[i] > from && after[i] < to);
      if (!near) continue;
      playing.push({ el: rows[i], dy: dy });
    }
    if (!playing.length) return;

    for (var j = 0; j < playing.length; j++) {
      var s = playing[j].el.style;
      s.transition = "none";
      s.transform = "translate3d(0," + playing[j].dy + "px,0)";
      s.willChange = "transform";
      playing[j].el.classList.add("is-sliding");
    }

    /* Read once so the browser takes the inverted positions as the
       starting state rather than folding them into the next paint. */
    void groups.offsetHeight;

    requestAnimationFrame(function () {
      for (var k = 0; k < playing.length; k++) {
        playing[k].el.style.transition = "transform " + MOVE_MS + "ms " + EASE;
        playing[k].el.style.transform = "";
      }
      /* One timer for the whole set, not a listener per row: the
         cleanup is the same for all of them and a transitionend that
         never fires — an interrupted change, a backgrounded tab —
         would otherwise leave will-change on for good. */
      setTimeout(function () {
        for (var m = 0; m < playing.length; m++) {
          var st = playing[m].el.style;
          st.transition = "";
          st.transform = "";
          st.willChange = "";
          playing[m].el.classList.remove("is-sliding");
        }
      }, MOVE_MS + 60);
    });
  }

  /* The headline changes at the same time, so it fades rather than
     cutting mid-sentence while the rows are still travelling. */
  function swapHead(id, then) {
    var top = root.querySelector("[data-shop-top]");
    if (!top || !root.querySelector("[data-shop-h]")) {
      paintHead(id);
      then();
      return;
    }
    top.classList.add("is-swap");
    setTimeout(function () {
      paintHead(id);
      top.classList.remove("is-swap");
      then();
    }, 150);
  }

  function changeRole(id) {
    /* Nothing that hides or moves content may depend on a frame
       callback in a page that is not being drawn. Same rule as the
       CoffeeOS page, learned the same way. */
    if (reduced() || D.hidden) { paint(id); return; }

    var glide = window.OS && window.OS.glideTo;
    var top = groups ? docTop(groups) - 90 : 0;

    /* If the rows are above the fold there is nothing to watch, so
       the page comes to them first — the same easing the CoffeeOS
       page uses, from the same function. */
    if (groups && glide && window.pageYOffset > top + 40) {
      var dist = window.pageYOffset - top;
      var ms = Math.max(300, Math.min(680, dist * 0.4));
      swapHead(id, function () {});
      glide(Math.max(0, top), ms, function () { slide(id); });
      return;
    }
    swapHead(id, function () { slide(id); });
  }

  function currentRole() {
    if (window.OS && typeof window.OS.currentId === "function") {
      return window.OS.currentId();
    }
    try {
      var saved = JSON.parse(localStorage.getItem("difluid_persona") || "null");
      if (saved && saved.id) return saved.id;
      if (typeof saved === "string") return saved;
    } catch (e) {}
    return "default";
  }

  /* ── Categories, in the page ────────────────────────────────
     A chip used to be a link to a collection, and a collection page
     is the old theme layout with no rail on it: one click in, and
     the only way back out is the browser's back button. So the
     chips now filter the catalogue where it already is. Nothing
     reloads, the rail stays put, and flipping from one category to
     the next is a click rather than two page loads.

     The href on each chip is still the real collection URL, so the
     rail works with no JavaScript and a crawler follows it to the
     collection. What changes is what happens on a click.

     The URL becomes ?c=<handle> rather than the collection's own
     address, because the collection's address renders a different
     page. Every URL this rail produces has to bring you back to
     what you were looking at, which is this page. */
  var cats = (function () {
    var node = D.querySelector("[data-shop-cats]");
    if (!node) return null;
    try {
      var d = JSON.parse(node.textContent);
      if (d && d.cats) { delete d.cats._; return d.cats; }
    } catch (e) {}
    return null;
  })();

  var allSec  = root.querySelector("[data-shop-all]");
  var allGrid = root.querySelector("[data-shop-allgrid]");
  var allH    = root.querySelector("[data-shop-allh]");
  var allSub  = root.querySelector("[data-shop-allsub]");
  var none    = root.querySelector("[data-shop-none]");
  var clear   = root.querySelector("[data-shop-clear]");
  var nav     = root.querySelector("[data-shop-nav]");

  /* A filter can only hide what is on the page. If the catalogue
     has outgrown one page the grid says so, and the chips are left
     as the plain links they have always been. */
  var canFilter = !!(cats && allSec && allGrid && nav &&
                     allGrid.getAttribute("data-complete") === "true");

  var picked = null;

  /* The chip the page opens on, and the chip it was asked to open on
     by the URL it was reached through.

     LANDING is a setting. PRESELECT is only set when the reader
     arrived on a collection URL, which is what a search result looks
     like: /collections/best-sellers has to come up as the shop with
     Best sellers lit, not as a page of its own that every other chip
     leads away from. */
  var LANDING   = root.getAttribute("data-shop-landing") || "";
  var PRESELECT = root.getAttribute("data-shop-preselect") || "";

  /* A chip's number comes from the collection; the grid it filters is
     the catalogue. Those can differ — a product can sit in a category
     and not in the catalogue collection — and a chip that promises
     four and shows three is the kind of small lie that makes a page
     feel broken. So once filtering is on, each chip counts what
     clicking it will actually show. */
  function trueCounts() {
    var have = {};
    var cards = allGrid.querySelectorAll(".os-p[data-handle]");
    for (var i = 0; i < cards.length; i++) have[cards[i].getAttribute("data-handle")] = 1;

    /* Everything counts the grid itself, which is the one number on
       the rail that cannot come from a collection. */
    var allChip = nav.querySelector('.os-chip[data-handle=""] span');
    if (allChip) allChip.textContent = cards.length;

    var chips = nav.querySelectorAll(".os-chip[data-handle]");
    for (var c = 0; c < chips.length; c++) {
      var handle = chips[c].getAttribute("data-handle");
      if (!handle) continue;
      var cat = cats[handle];
      var span = chips[c].querySelector("span");
      if (!cat || !span) continue;
      var n = 0;
      for (var k = 0; k < cat.items.length; k++) if (have[cat.items[k]]) n++;
      cat.shown = n;
      span.textContent = n;
      /* A category with nothing on this page would filter to an empty
         grid. It stays readable and stops being clickable. */
      chips[c].classList.toggle("is-empty", n === 0);
    }
  }

  function showCategory(handle, opts) {
    if (!canFilter) return false;
    var cat = handle && cats[handle];
    if (handle && !cat) return false;
    picked = handle || null;

    var chips = nav.querySelectorAll(".os-chip");
    var onChip = null;
    for (var i = 0; i < chips.length; i++) {
      var hit = (chips[i].getAttribute("data-handle") || "") === (handle || "");
      chips[i].classList.toggle("is-on", hit);
      if (hit) onChip = chips[i];
    }

    /* The rail is fifteen chips on a scrolling strip, so the selected
       one can easily be off to the side. Bring it into the strip, and
       only along the strip — scrollIntoView would drag the page. */
    if (onChip && nav.scrollWidth > nav.clientWidth) {
      /* Measured on screen, so it centres the chip the same way when the
         strip runs right to left; the browser keeps it inside the strip. */
      var cr = onChip.getBoundingClientRect(), nr = nav.getBoundingClientRect();
      nav.scrollLeft += (cr.left + cr.width / 2) - (nr.left + nr.width / 2);
    }

    /* The group rows are the shop's own argument for a reader with no
       category in mind. Once one is chosen they are in the way. */
    if (groups) groups.hidden = !!handle;

    /* The bundles band and the spotlights are not the same thing.
       They stay on the view the page opens on as well as on
       Everything, because that first screen is the one everybody
       sees and the band is the block meant to sell from. They go
       away only once the reader has gone looking for a category. */
    var onLanding = !handle || handle === LANDING;
    var hubOnly = root.querySelectorAll("[data-shop-hubonly]");
    for (var q = 0; q < hubOnly.length; q++) hubOnly[q].hidden = !onLanding;

    var want = null;
    if (cat) {
      want = {};
      for (var n = 0; n < cat.items.length; n++) want[cat.items[n]] = 1;
    }
    var shown = 0;
    var cards = allGrid.querySelectorAll(".os-p[data-handle]");
    for (var k = 0; k < cards.length; k++) {
      var on = !want || want[cards[k].getAttribute("data-handle")] === 1;
      cards[k].hidden = !on;
      if (on) shown++;
    }

    if (allH) allH.textContent = cat ? cat.label : T("shop.everything", "Everything");
    if (allSub) {
      allSub.textContent = cat
        ? (shown === 1
            ? T("shop.count_in_one", "{n} product in {category}.")
            : T("shop.count_in_other", "{n} products in {category}."))
            .replace("{n}", shown).replace("{category}", function () { return cat.label; })
        : T("shop.all_count", "All {n} products, devices and spares together.").replace("{n}", cards.length);
    }
    if (none) none.hidden = shown !== 0;
    if (clear) clear.hidden = !handle;
    root.classList.toggle("is-filtered", !!handle);

    if (opts && opts.scroll) {
      var top = docTop(nav) - 90;
      try { window.scrollTo({ top: top, behavior: reduced() ? "auto" : "smooth" }); }
      catch (e) { window.scrollTo(0, top); }
    }
    return true;
  }

  /* Three states, and the URL has to tell them apart. Everything is
     written as ?c=all rather than as a bare path, because a bare path
     is what an arrival looks like and an arrival opens on the landing
     chip. Without the marker, choosing Everything and then reloading
     would put the reader back on Best sellers.

     Returns undefined when the URL says nothing at all. */
  function fromUrl() {
    var m = /[?&]c=([^&#]+)/.exec(window.location.search);
    if (!m) return undefined;
    var v = decodeURIComponent(m[1]);
    return v === "all" ? "" : v;
  }

  /* What an arrival with nothing in the URL should show: the chip the
     collection URL named, else the landing chip, else Everything. A
     landing chip holding nothing that is on this page would open on
     an empty grid, so it is checked before it is used. */
  function arrival() {
    if (PRESELECT && cats[PRESELECT]) return PRESELECT;
    if (LANDING && cats[LANDING] && cats[LANDING].shown !== 0) return LANDING;
    return "";
  }

  function wanted() {
    var u = fromUrl();
    return u === undefined ? arrival() : u;
  }

  function go(handle, push) {
    if (!showCategory(handle, { scroll: true })) return false;
    if (push && window.history && window.history.pushState) {
      var url = window.location.pathname + "?c=" + encodeURIComponent(handle || "all");
      window.history.pushState({ c: handle || null }, "", url);
    }
    return true;
  }

  if (canFilter) {
    nav.addEventListener("click", function (e) {
      /* Let a modified click open the collection in a tab, which is
         what the reader asked for by holding the key. */
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      var chip = e.target.closest ? e.target.closest(".os-chip") : null;
      if (!chip || !nav.contains(chip)) return;
      var handle = chip.getAttribute("data-handle") || "";
      if (go(handle, true)) e.preventDefault();
    });

    if (clear) {
      clear.addEventListener("click", function (e) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        if (go("", true)) e.preventDefault();
      });
    }

    window.addEventListener("popstate", function () {
      showCategory(wanted(), { scroll: false });
    });

    /* A role change reorders the group rows, which are hidden while a
       category is selected. Re-apply afterwards so the selection
       survives the persona swap rather than quietly coming undone. */
    D.addEventListener("os:role", function () {
      if (!picked) return;
      window.setTimeout(function () { showCategory(picked, { scroll: false }); }, 0);
    });

    /* trueCounts first: arrival() asks whether the landing chip has
       anything on this page, and that number does not exist until
       the counts have been taken. */
    trueCounts();
    showCategory(wanted(), { scroll: false });
  }

  /* Only a change animates. os.js fires os:role once on load with
     whatever role is already stored, and this file has usually
     painted that role a moment earlier — so the test is whether the
     role is different, not whether an event has been seen before.
     Anything else animates the page on arrival, which is a load
     cost dressed up as a flourish. */
  var showing = null;

  D.addEventListener("os:role", function (e) {
    var id = (e.detail && e.detail.role) || currentRole();
    if (id === showing) return;
    if (showing === null) { paint(id); showing = id; return; }
    showing = id;
    changeRole(id);
  });

  /* os.js is deferred too and may paint after this file runs, so
     paint once now and let the event correct it if it differs. */
  showing = currentRole();
  paint(showing);
})();
